import { FormControlLabel, Radio } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React from 'react';
import {UseFormReturn} from 'react-hook-form';

import ToggleDisplayEditFormItem from '../../../../components/ToggleDisplayEditFormItem';
import { ContactNumberFormItem } from '../../../../components/form-items/ContactNumberFormItem';
import { disabledInput } from '../../logic/useUpdateRegistrationParticulars';
import { getIExamMasterCodeSelected } from '../../../profile/logic/useCandidateProfileForm';
import HideOrShow from '../../../profile/components/CandidateProfileForm/HelperComponents/HideOrShow';
import { useStyles } from './style';
import { ContactDetailsItem } from '../../../../api/candidate/exam/model';
import { getContactDisplayValue } from '../../../../utils/ComponentUtils';
import { CandidateProfileFormDefaultValues } from '../../../../models';
import {
  ControllerRadioGroupWrapper
} from "../../../profile/components/CandidateProfileForm/HelperComponents/ControllerRadioGroupWrapper";

const halfWidthLeftItemContainer = (
  isEditState: boolean,
  classes: Record<string,string>,
  sameRowPhone: boolean
) =>
  isEditState
    ? `${classes.contactNumContainer}`
    : `${classes.contactNumContainer} ${sameRowPhone && classes.halfWidthLeftItem}`;

const halfWidthRightItemContainer = (
  isEditState: boolean,
  classes: Record<string,string>,
  sameRowPhone: boolean
) =>
  isEditState
    ? `${classes.contactNumContainer}`
    : `${classes.contactNumContainer} ${sameRowPhone && classes.halfWidthRightItem}`;

const radioGroupRow = (isEditState: boolean, sameRowPhone: boolean) =>
  isEditState ? false : sameRowPhone;

const usePhoneNumber = (contactDetails: ContactDetailsItem | undefined) =>
  getContactDisplayValue(
    false,
    contactDetails?.phoneNumber?.countryCode.codeValueDescription,
    contactDetails?.phoneNumber?.number
  );
const useMobileNumber = (contactDetails: ContactDetailsItem | undefined) =>
  getContactDisplayValue(
    false,
    contactDetails?.mobileNumber?.countryCode.codeValueDescription,
    contactDetails?.mobileNumber?.number
  );

interface MobileTelephoneContactDetailsProps {
  contactDetails: ContactDetailsItem | undefined;
  form?: UseFormReturn<Record<string, any>>;
  isEditState: boolean;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  sameRowPhone: boolean;
}

const MobileTelephoneContactDetails = ({
  contactDetails,
  form,
  isEditState,
  formDefaultValues,
  sameRowPhone,
}: MobileTelephoneContactDetailsProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const phoneVal = usePhoneNumber(contactDetails);
  const mobileVal = useMobileNumber(contactDetails);

  return (
      <ControllerRadioGroupWrapper
          name="preferredTelephoneOrMobile"
          control={form?.control}
          defaultValue={formDefaultValues?.preferredTelephoneOrMobile ?? 'M'}
          row={radioGroupRow(isEditState, sameRowPhone)}
      >
        <ToggleDisplayEditFormItem
            id="mobileNo"
            isEditState={isEditState}
            labelText={t('components->>ContactDetails.Mobile No.')}
            isPreferredLabelText={contactDetails?.mobileNumberPreferred ? 'Preferred Contact' : ''}
            valueText={mobileVal}
            containerClassName={halfWidthLeftItemContainer(isEditState, classes, sameRowPhone)}
            EditComponent={
                form && (
                    <ContactNumberFormItem
                        id="candidateProfileMobile"
                        formHook={form}
                        label={t('components->>ContactDetails.Mobile No.')}
                        countryCode={{
                          name: 'mobileCountryCode',
                          error: !!form.formState.errors.mobileCountryCode,
                          helperText: form.formState.errors?.mobileCountryCode?.message?.toString(),
                          disabled: disabledInput?.mobileCountryCode,
                        }}
                        countryCodeisOptionEqualToValue={getIExamMasterCodeSelected}
                        number={{
                          name: 'mobileContactNumber',
                          error: !!form.formState.errors.mobileContactNumber,
                          helperText: form.formState.errors?.mobileContactNumber?.message?.toString(),
                          disabled: disabledInput?.mobileContactNumber,
                        }}
                        isSkipTransform
                        countryCodeDefaultValue={formDefaultValues?.mobileCountryCode}
                        numberDefaultValue={formDefaultValues?.mobileContactNumber}
                        isRequired
                    />
                )
            }
        />
        <HideOrShow
            isHide={!isEditState}
            Component={
              <FormControlLabel
                  value="M"
                  name="preferredTelephoneOrMobile"
                  control={<Radio />}
                  label={t('components->>ContactDetails.SetPreferredContact')}
                  className={classes.formElementContainer}
                  disabled={disabledInput?.preferredTelephoneOrMobile}
              />
            }
        />
        <ToggleDisplayEditFormItem
            isEditState={isEditState}
            labelText={t('components:ContactDetails.Telephone No.')}
            valueText={phoneVal}
            containerClassName={halfWidthRightItemContainer(isEditState, classes, sameRowPhone)}
            id="candidateProfileTelephone"
            isPreferredLabelText={!contactDetails?.mobileNumberPreferred ? 'Preferred Contact' : ''}
            EditComponent={
                form && (
                    <ContactNumberFormItem
                        id="candidateProfileTelephone"
                        formHook={form}
                        label={t('components:ContactDetails.Telephone No.')}
                        countryCode={{
                          name: 'telephoneCountryCode',
                          error: !!form.formState.errors.telephoneCountryCode,
                          helperText: form.formState.errors?.telephoneCountryCode?.message?.toString(),
                          disabled: disabledInput?.telephoneCountryCode,
                        }}
                        countryCodeisOptionEqualToValue={getIExamMasterCodeSelected}
                        number={{
                          name: 'telephoneContactNumber',
                          error: !!form.formState.errors.telephoneContactNumber,
                          helperText: form.formState.errors?.telephoneContactNumber?.message?.toString(),
                          disabled: disabledInput?.telephoneContactNumber,
                        }}
                        isSkipTransform
                        placeholder="Enter Phone No."
                        countryCodeDefaultValue={formDefaultValues?.telephoneCountryCode}
                        numberDefaultValue={formDefaultValues?.telephoneContactNumber}
                    />
                )
            }
        />
        <HideOrShow
            isHide={!isEditState}
            Component={
              <FormControlLabel
                  value="T"
                  name="preferredTelephoneOrMobile"
                  control={<Radio />}
                  label={t('components->>ContactDetails.SetPreferredContact')}
                  className={classes.formElementContainer}
                  disabled={disabledInput?.preferredTelephoneOrMobile}
              />
            }
        />
      </ControllerRadioGroupWrapper>

  );
};

export default MobileTelephoneContactDetails;
